// Disable aggressive page prefetching from Blockshop theme
// The theme prefetches every internal link on the page as a full document,
// generating 50-100+ unnecessary requests per page load.
// Patch runs immediately (not on load) — IntersectionObserver fires before the load event.
(function patchLinkLoader() {
  if (typeof Utils !== 'undefined' && Utils.linkLoader && !Utils._linkLoaderPatched) {
    const _orig = Utils.linkLoader.bind(Utils);
    Utils.linkLoader = function(url, rel, priority, as) {
      if (rel === 'prefetch' && as === 'document') return;
      return _orig(url, rel, priority, as);
    };
    Utils._linkLoaderPatched = true;
  }
})();
document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('link[rel="prefetch"][as="document"]').forEach(el => el.remove());
});

// Collection SEO description — expandable text
class CollectionSeoDescription extends HTMLElement {
  connectedCallback() {
    this.body = this.querySelector('.collection-seo--body');
    this.content = this.querySelector('.collection-seo--content');
    this.toggle = this.querySelector('[collection-seo-toggle]');
    this.mode = this.body.dataset.truncate;
    this.collapsedHeight = 0;

    const firstHeading = this.content.querySelector('h1,h2,h3,h4,h5,h6');
    if (firstHeading) firstHeading.dataset.item = 'section-heading';

    this.toggle.addEventListener('click', () => {
      if (this.body.dataset.expanded === 'true') this.collapse();
      else this.expand();
    });

    this.init();
  }

  getCollapsedHeight() {
    if (this.mode === 'first-paragraph') {
      const firstP = this.content.querySelector('p');
      if (firstP) return firstP.offsetTop + firstP.offsetHeight;
      return Math.round(parseFloat(getComputedStyle(this.content).lineHeight) * 3);
    }
    const targetHeight = Math.round(parseFloat(getComputedStyle(this.content).lineHeight) * 6);
    let snappedHeight = 0;
    let lastFitP = null;
    for (const p of this.content.querySelectorAll(':scope > p')) {
      const bottom = p.offsetTop + p.offsetHeight;
      if (bottom <= targetHeight) { snappedHeight = bottom; lastFitP = p; }
      else break;
    }
    if (lastFitP) {
      const mb = parseFloat(getComputedStyle(lastFitP).marginBottom) || 0;
      snappedHeight += mb;
    }
    return snappedHeight > 0 ? snappedHeight : targetHeight;
  }

  init() {
    this.collapsedHeight = this.getCollapsedHeight();
    if (this.content.scrollHeight <= this.collapsedHeight + 16) {
      this.content.style.blockSize = 'auto';
      this.dataset.noToggle = '';
      return;
    }
    this.content.style.blockSize = this.collapsedHeight + 'px';
  }

  expand() {
    this.content.style.blockSize = this.content.scrollHeight + 'px';
    this.body.dataset.expanded = 'true';
    this.toggle.setAttribute('aria-expanded', 'true');
    this.content.addEventListener('transitionend', function onEnd() {
      this.removeEventListener('transitionend', onEnd);
      this.style.blockSize = 'auto';
    });
  }

  collapse() {
    this.content.style.blockSize = this.content.scrollHeight + 'px';
    this.body.dataset.expanded = 'false';
    this.toggle.setAttribute('aria-expanded', 'false');
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.collapsedHeight = this.getCollapsedHeight();
        this.content.style.blockSize = this.collapsedHeight + 'px';
      });
    });
  }
}
if (!customElements.get('collection-seo-description'))
  customElements.define('collection-seo-description', CollectionSeoDescription);

// Coupon code widget
class CouponCodeWidget extends HTMLElement {
  connectedCallback() {
    const btn = this.querySelector('.coupon-code--copy');
    const labelCopy = btn.querySelector('[coupon-label-copy]');
    const labelCopied = btn.querySelector('[coupon-label-copied]');
    const code = this.dataset.code;

    btn.addEventListener('click', () => {
      if (btn.hasAttribute('data-copied')) return;
      navigator.clipboard.writeText(code).then(() => {
        labelCopy.hidden = true;
        labelCopied.hidden = false;
        btn.dataset.copied = '';
        setTimeout(() => {
          labelCopy.hidden = false;
          labelCopied.hidden = true;
          btn.removeAttribute('data-copied');
        }, 2000);
      });
    });
  }
}
if (!customElements.get('coupon-code-widget'))
  customElements.define('coupon-code-widget', CouponCodeWidget);

class MenuMobile extends HTMLElement {
  constructor() {
    super();
    this.isOpen = false;
    this.accordions = [];
  }

  connectedCallback() {
    this.setAttribute('aria-hidden', 'true');
    this.setAttribute('role', 'dialog');
    this.setAttribute('aria-modal', 'true');
    
    this.setupAccordion();
    this.setupEventListeners();
    this.setupGlobalListeners();
    this.setupSlider();
  }

  setupAccordion() {
    const toggleButtons = this.querySelectorAll('.menu-mobile__toggle[data-action="toggle-accordion"]');
    
    toggleButtons.forEach(button => {
      const targetId = button.getAttribute('data-target');
      const submenu = this.querySelector(`[data-submenu="${targetId}"]`);
      
      if (submenu) {
        this.accordions.push({
          button,
          submenu,
          targetId,
          isOpen: false
        });
        
        submenu.style.height = '0';
        submenu.style.overflow = 'hidden';
      }
    });
  }

  setupEventListeners() {
    const toggleButtons = this.querySelectorAll('[data-action="toggle-accordion"]');
    toggleButtons.forEach(button => {
      button.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        const targetId = button.getAttribute('data-target');
        this.toggleAccordion(targetId);
      });
    });
    
    const closeButtons = this.querySelectorAll('[data-action="close-mobile-menu"]');
    closeButtons.forEach(button => {
      button.addEventListener('click', (e) => {
        e.preventDefault();
        this.close();
      });
    });
  }

  setupGlobalListeners() {
    // Toggle buttons (outside the menu component, e.g., in header)
    const mobileMenuToggles = document.querySelectorAll('[data-action="toggle-mobile-menu"]');
    mobileMenuToggles.forEach(button => {
      button.addEventListener('click', (e) => {
        e.preventDefault();
        this.toggle();
      });
    });
  }

  setupSlider() {
    const sliderEl = this.querySelector('.menu-mobile__slider');
    if (!sliderEl) return;
    
    new MenuMobileSlider(sliderEl);
  }

  toggleAccordion(targetId) {
    const accordion = this.accordions.find(acc => acc.targetId === targetId);
    if (!accordion) return;
    
    if (accordion.isOpen) {
      this.closeAccordion(accordion);
    } else {
      this.openAccordion(accordion);
    }
  }

  openAccordion(accordion) {
    const { button, submenu } = accordion;
    
    // Get the natural height
    submenu.style.height = 'auto';
    const height = submenu.scrollHeight;
    submenu.style.height = '0';
    
    // Trigger reflow
    submenu.offsetHeight;
    
    // Animate to full height
    submenu.style.transition = 'height 0.3s ease';
    submenu.style.height = `${height}px`;
    
    accordion.isOpen = true;
    button.setAttribute('aria-expanded', 'true');
    button.closest('li').classList.add('is-open');
    
    // Remove height after transition for responsiveness
    submenu.addEventListener('transitionend', function onTransitionEnd() {
      if (accordion.isOpen) {
        submenu.style.height = 'auto';
      }
      submenu.removeEventListener('transitionend', onTransitionEnd);
    });
  }

  closeAccordion(accordion) {
    const { button, submenu } = accordion;
    
    // Set explicit height before animating
    const height = submenu.scrollHeight;
    submenu.style.height = `${height}px`;
    
    // Trigger reflow
    submenu.offsetHeight;
    
    // Animate to 0
    submenu.style.transition = 'height 0.3s ease';
    submenu.style.height = '0';
    
    accordion.isOpen = false;
    button.setAttribute('aria-expanded', 'false');
    button.closest('li').classList.remove('is-open');
  }

  open() {
    if (this.isOpen) return;
    
    this.isOpen = true;
    this.setAttribute('aria-hidden', 'false');
    this.classList.add('is-open');
    document.body.style.overflow = 'hidden';
    
    this.dispatchEvent(new CustomEvent('menu-mobile:open', {
      bubbles: true,
      detail: { element: this }
    }));
  }

  close() {
    if (!this.isOpen) return;
    
    this.isOpen = false;
    this.setAttribute('aria-hidden', 'true');
    this.classList.remove('is-open');
    document.body.style.overflow = '';
    
    this.dispatchEvent(new CustomEvent('menu-mobile:close', {
      bubbles: true,
      detail: { element: this }
    }));
  }

  toggle() {
    if (this.isOpen) {
      this.close();
    } else {
      this.open();
    }
  }
}

class MenuMobileSlider {
  constructor(element) {
    this.slider = element;
    this.track = this.slider.querySelector('.menu-mobile__slider-track');
    this.slides = Array.from(this.slider.querySelectorAll('.menu-mobile__slide'));
    this.prevArrow = this.slider.querySelector('.menu-mobile__slider-arrow--prev');
    this.nextArrow = this.slider.querySelector('.menu-mobile__slider-arrow--next');
    
    if (this.slides.length === 0) return;
    
    // Settings from data attributes or defaults
    this.currentIndex = 0;
    this.autoplayDelay = parseInt(this.slider.dataset.autoplayDelay) || 5000;
    this.transitionSpeed = parseInt(this.slider.dataset.transitionSpeed) || 500;
    this.loop = this.slider.dataset.loop === 'true';
    this.autoplayEnabled = this.autoplayDelay > 0;
    
    this.isTransitioning = false;
    this.autoplayTimer = null;
    
    this.init();
  }
  
  init() {
    this.updateSlider(false);
    this.addEventListeners();
    
    if (this.autoplayEnabled) {
      this.startAutoplay();
    }
  }
  
  addEventListeners() {
    // Arrow navigation
    if (this.prevArrow) {
      this.prevArrow.addEventListener('click', () => {
        this.prev();
      });
    }
    
    if (this.nextArrow) {
      this.nextArrow.addEventListener('click', () => {
        this.next();
      });
    }
    
    // Touch/swipe support
    let startX = 0;
    let currentX = 0;
    let isDragging = false;
    
    // Touch events
    this.track.addEventListener('touchstart', (e) => {
      startX = e.touches[0].clientX;
      isDragging = true;
      this.pauseAutoplay();
    }, { passive: true });
    
    this.track.addEventListener('touchmove', (e) => {
      if (!isDragging) return;
      currentX = e.touches[0].clientX;
    }, { passive: true });
    
    this.track.addEventListener('touchend', () => {
      if (!isDragging) return;
      isDragging = false;
      
      const diff = startX - currentX;
      if (Math.abs(diff) > 50) {
        if (diff > 0) {
          this.next();
        } else {
          this.prev();
        }
      }
      
      if (this.autoplayEnabled) {
        this.startAutoplay();
      }
    }, { passive: true });
    
    // Mouse drag support
    this.track.addEventListener('mousedown', (e) => {
      e.preventDefault();
      startX = e.clientX;
      isDragging = true;
      this.track.style.cursor = 'grabbing';
      this.pauseAutoplay();
    });
    
    this.track.addEventListener('mousemove', (e) => {
      if (!isDragging) return;
      e.preventDefault();
      currentX = e.clientX;
    });
    
    this.track.addEventListener('mouseup', () => {
      if (!isDragging) return;
      isDragging = false;
      this.track.style.cursor = 'grab';
      
      const diff = startX - currentX;
      if (Math.abs(diff) > 50) {
        if (diff > 0) {
          this.next();
        } else {
          this.prev();
        }
      }
      
      if (this.autoplayEnabled) {
        this.startAutoplay();
      }
    });
    
    this.track.addEventListener('mouseleave', () => {
      if (isDragging) {
        isDragging = false;
        this.track.style.cursor = 'grab';
        if (this.autoplayEnabled) {
          this.startAutoplay();
        }
      }
    });
    
    // Set initial cursor
    this.track.style.cursor = 'grab';
    
    // Pause on hover
    this.slider.addEventListener('mouseenter', () => this.pauseAutoplay());
    this.slider.addEventListener('mouseleave', () => {
      if (this.autoplayEnabled) this.startAutoplay();
    });
  }
  
  goToSlide(index, animate = true) {
    if (this.isTransitioning) return;
    
    if (index < 0) {
      index = this.loop ? this.slides.length - 1 : 0;
    } else if (index >= this.slides.length) {
      index = this.loop ? 0 : this.slides.length - 1;
    }
    
    this.currentIndex = index;
    this.updateSlider(animate);
    this.resetAutoplay();
  }
  
  updateSlider(animate = true) {
    const offset = -this.currentIndex * 100;
    
    if (animate) {
      this.isTransitioning = true;
      this.track.style.transition = `transform ${this.transitionSpeed}ms ease-in-out`;
      setTimeout(() => {
        this.isTransitioning = false;
      }, this.transitionSpeed);
    } else {
      this.track.style.transition = 'none';
    }
    
    this.track.style.transform = `translateX(${offset}%)`;
  }
  
  next() {
    this.goToSlide(this.currentIndex + 1);
  }
  
  prev() {
    this.goToSlide(this.currentIndex - 1);
  }
  
  startAutoplay() {
    if (!this.autoplayEnabled) return;
    this.pauseAutoplay();
    this.autoplayTimer = setInterval(() => {
      this.next();
    }, this.autoplayDelay);
  }
  
  pauseAutoplay() {
    if (this.autoplayTimer) {
      clearInterval(this.autoplayTimer);
      this.autoplayTimer = null;
    }
  }
  
  resetAutoplay() {
    if (this.autoplayEnabled) {
      this.pauseAutoplay();
      this.startAutoplay();
    }
  }
}

customElements.define('menu-mobile', MenuMobile);

class MenuDesktop extends HTMLElement {
  constructor() {
    super();
    this.activeTab = null;
    this.tabs = [];
    this.tabPanels = [];
    this.closeTimeout = null;
    this.initialized = false;
  }

  connectedCallback() {
    this.setAttribute('role', 'navigation');
    this.init();
  }

  init() {
    if (this.initialized) return;
    
    this.tabs = Array.from(this.querySelectorAll('.menu-desktop-bar li[data-menu-desktop-tab]'));
    this.tabPanels = Array.from(this.querySelectorAll('.menu-desktop-tabs > ul > li[data-menu-desktop-tab]'));
    
    // Check if elements were found
    if (this.tabs.length === 0 || this.tabPanels.length === 0) {
      console.warn('MenuDesktop: No menu elements found, check if menu-desktop.liquid snippet is rendered');
      return;
    }
    
    this.initialized = true;
    
    this.tabs.forEach((tab) => {
      const link = tab.querySelector('a, span.link-empty');
      const tabId = tab.getAttribute('data-menu-desktop-tab');
      if (link) {
        link.setAttribute('aria-expanded', 'false');
        link.setAttribute('aria-controls', `tab-panel-${tabId}`);
      }
    });

    this.tabPanels.forEach((panel) => {
      const tabId = panel.getAttribute('data-menu-desktop-tab');
      panel.setAttribute('id', `tab-panel-${tabId}`);
      panel.setAttribute('role', 'region');
      panel.setAttribute('aria-hidden', 'true');
    });

    this.setupHoverHandlers();
    this.updateMaxHeight();

    this._resizeHandler = () => {
      clearTimeout(this._resizeTimer);
      this._resizeTimer = setTimeout(() => this.updateMaxHeight(), 100);
    };
    window.addEventListener('resize', this._resizeHandler);
  }

  updateMaxHeight() {
    const tabs = this.querySelector('.menu-desktop-tabs');
    if (!tabs) return;
    const maxH = window.innerHeight - this.getBoundingClientRect().bottom;
    tabs.style.maxHeight = maxH > 0 ? maxH + 'px' : '';
  }

  setupHoverHandlers() {
    // Collect all links in menu bar (with and without submenu)
    const allBarLinks = Array.from(this.querySelectorAll('.menu-desktop-bar a'));
    
    // Hover on li element - opens tab
    this.tabs.forEach((tab, index) => {
      const tabId = tab.getAttribute('data-menu-desktop-tab');
      const link = tab.querySelector('a, span.link-empty');
      
      // Mouse hover
      tab.addEventListener('mouseenter', () => {
        // Cancel closing timeout if exists
        if (this.closeTimeout) {
          clearTimeout(this.closeTimeout);
          this.closeTimeout = null;
        }
        
        // Check if panel exists before opening
        const panel = this.tabPanels.find(p => p.getAttribute('data-menu-desktop-tab') === tabId);
        
        if (panel) {
          this.openTab(tabId);
        } else {
          console.log('No panel for tab:', tabId);
        }
      });
      
      // Keyboard focus
      if (link) {
        link.addEventListener('focus', () => {
          // Cancel closing timeout
          if (this.closeTimeout) {
            clearTimeout(this.closeTimeout);
            this.closeTimeout = null;
          }
          
          // Open tab if it has a panel
          const panel = this.tabPanels.find(p => p.getAttribute('data-menu-desktop-tab') === tabId);
          if (panel) {
            this.openTab(tabId);
          }
        });
        
        // Handle Left/Right arrow keys for navigation
        link.addEventListener('keydown', (e) => {
          if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
            e.preventDefault();
            
            const currentIndex = index;
            let nextIndex;
            
            if (e.key === 'ArrowRight') {
              nextIndex = currentIndex + 1;
              if (nextIndex >= this.tabs.length) nextIndex = 0;
            } else {
              nextIndex = currentIndex - 1;
              if (nextIndex < 0) nextIndex = this.tabs.length - 1;
            }
            
            const nextTab = this.tabs[nextIndex];
            const nextLink = nextTab?.querySelector('a, span.link-empty');
            if (nextLink) {
              nextLink.focus();
            }
          }
          
          // Arrow down - enter submenu
          if (e.key === 'ArrowDown') {
            e.preventDefault();
            
            const panel = this.tabPanels.find(p => p.getAttribute('data-menu-desktop-tab') === tabId);
            if (panel && panel.classList.contains('is-open')) {
              const firstLink = panel.querySelector('a');
              if (firstLink) {
                firstLink.focus();
              }
            }
          }
          
          // Escape closes tab
          if (e.key === 'Escape') {
            this.closeTab();
            link.focus();
          }
        });
      }
    });
    
    // Handle focus and hover on elements without submenu
    const noSubmenuItems = Array.from(this.querySelectorAll('.menu-desktop-bar li:not(.has-items)'));
    
    noSubmenuItems.forEach((li) => {
      const link = li.querySelector('a');
      
      if (link) {
        // Focus on link without submenu - close tabs
        link.addEventListener('focus', () => {
          // Cancel closing timeout
          if (this.closeTimeout) {
            clearTimeout(this.closeTimeout);
            this.closeTimeout = null;
          }
          
          this.closeTab();
        });
      }
      
      // Hover on li without submenu - close tabs
      li.addEventListener('mouseenter', () => {
        // Cancel closing timeout
        if (this.closeTimeout) {
          clearTimeout(this.closeTimeout);
          this.closeTimeout = null;
        }
        
        this.closeTab();
      });
    });
    
    // Handle navigation in submenu panels
    this.tabPanels.forEach((panel) => {
      const links = Array.from(panel.querySelectorAll('a'));
      
      links.forEach((link, linkIndex) => {
        link.addEventListener('keydown', (e) => {
          // Arrow up on first element - return to menu bar
          if (e.key === 'ArrowUp') {
            if (linkIndex === 0) {
              e.preventDefault();
              const tabId = panel.getAttribute('data-menu-desktop-tab');
              const parentTab = this.tabs.find(t => t.getAttribute('data-menu-desktop-tab') === tabId);
              const parentLink = parentTab?.querySelector('a');
              if (parentLink) {
                parentLink.focus();
              }
            } else {
              e.preventDefault();
              links[linkIndex - 1]?.focus();
            }
          }
          
          // Arrow down - next element in submenu
          if (e.key === 'ArrowDown') {
            e.preventDefault();
            if (linkIndex < links.length - 1) {
              links[linkIndex + 1].focus();
            }
          }
          
          // Escape closes tab and returns to parent
          if (e.key === 'Escape') {
            e.preventDefault();
            const tabId = panel.getAttribute('data-menu-desktop-tab');
            const parentTab = this.tabs.find(t => t.getAttribute('data-menu-desktop-tab') === tabId);
            const parentLink = parentTab?.querySelector('a');
            this.closeTab();
            if (parentLink) {
              parentLink.focus();
            }
          }
        });
      });
    });

    // Hover on entire menu-desktop - debounce closing
    this.addEventListener('mouseleave', () => {
      // Start timeout to close after 1.5s
      this.closeTimeout = setTimeout(() => {
        this.closeTab();
        this.closeTimeout = null;
      }, 500);
    });

    // Cancel closing when hovering back
    this.addEventListener('mouseenter', () => {
      if (this.closeTimeout) {
        clearTimeout(this.closeTimeout);
        this.closeTimeout = null;
      }
    });
    
    // Handle focus leaving menu (for keyboard navigation)
    this.addEventListener('focusout', (e) => {
      // Check if focus didn't move to another element in menu
      setTimeout(() => {
        if (!this.contains(document.activeElement)) {
          // Focus is outside menu - close tab
          this.closeTab();
        }
      }, 100);
    });
  }

  openTab(identifier) {
    this.updateMaxHeight();
    const tabId = String(identifier);
    
    // Find tab and panel by data-menu-desktop-tab attribute
    const tab = this.tabs.find(t => t.getAttribute('data-menu-desktop-tab') === tabId);
    const panel = this.tabPanels.find(p => p.getAttribute('data-menu-desktop-tab') === tabId);

    if (!tab || !panel) {
      // No panel means the link has no submenu - this is a normal situation
      return;
    }

    // Close currently active tab if different
    if (this.activeTab !== null && this.activeTab !== tabId) {
      this.closeTab();
    }

    // Skip if already active
    if (this.activeTab === tabId) {
      return;
    }

    // Open the new tab
    this.activeTab = tabId;
    const link = tab.querySelector('a');

    if (link) {
      link.setAttribute('aria-expanded', 'true');
    }
    
    panel.setAttribute('aria-hidden', 'false');
    panel.classList.add('is-open');
    tab.classList.add('is-open');
    
    const tabsContainer = this.querySelector('.menu-desktop-tabs');
    if (tabsContainer) {
      tabsContainer.classList.add('is-open');
    }

    this.dispatchEvent(new CustomEvent('menu-desktop:tab-open', {
      bubbles: true,
      detail: { tabId, element: this }
    }));
  }

  closeTab() {
    if (this.activeTab === null) return;

    const tabId = this.activeTab;
    const tab = this.tabs.find(t => t.getAttribute('data-menu-desktop-tab') === tabId);
    const panel = this.tabPanels.find(p => p.getAttribute('data-menu-desktop-tab') === tabId);

    if (!tab || !panel) return;

    const link = tab.querySelector('a');

    if (link) {
      link.setAttribute('aria-expanded', 'false');
    }

    panel.setAttribute('aria-hidden', 'true');
    panel.classList.remove('is-open');
    tab.classList.remove('is-open');
    
    const tabsContainer = this.querySelector('.menu-desktop-tabs');
    if (tabsContainer) {
      tabsContainer.classList.remove('is-open');
    }

    this.dispatchEvent(new CustomEvent('menu-desktop:tab-close', {
      bubbles: true,
      detail: { tabId, element: this }
    }));

    this.activeTab = null;
  }

  toggleTab(identifier) {
    const tabId = String(identifier);

    if (this.activeTab === tabId) {
      this.closeTab();
    } else {
      this.openTab(identifier);
    }
  }
}

if (!customElements.get('menu-desktop')) {
  customElements.define('menu-desktop', MenuDesktop);
}

class DrawerAlt extends HTMLElement {
  constructor() {
    super();

    this.triggers = [
      ...this.querySelectorAll("[drawer-trigger]"),
      ...document.querySelectorAll(`[external-drawer-trigger="${this.id}"]`),
    ];

    this.closers = this.querySelectorAll("[drawer-close]");
    this.drawer = this.querySelector("dialog");

    this._handleCloserClick = this._handleCloserClick.bind(this);
    this._handleDrawerClick = this._handleDrawerClick.bind(this);
  }

  connectedCallback() {
    this.triggers.forEach((trigger) => {
      trigger.addEventListener("click", (e) => {
        e.preventDefault();
        this.toggle();
      });
    });

    this.closers.forEach((closer) => {
      closer.addEventListener("click", this._handleCloserClick);
    });

    if (this.drawer) {
      this.drawer.addEventListener("click", this._handleDrawerClick);
    }
  }

  _handleCloserClick(event) {
    event.stopPropagation();
    event.preventDefault();

    const targetDialog = event.currentTarget.closest("dialog");
    if (targetDialog && targetDialog === this.drawer) this.close();
  }

  _handleDrawerClick(event) {
    if (event.target === this.drawer) this.close();
  }

  toggle() {
    if (this.drawer && this.drawer.open) this.close();
    else this.open();
  }

  close() {
    if (this.drawer) this.drawer.close();
  }

  open() {
    if (this.drawer) this.drawer.showModal();
  }
}

if (!customElements.get("drawer-alt"))
  customElements.define("drawer-alt", DrawerAlt);

class MarketSwitcher extends DrawerAlt {
  constructor() {
    super();

    this.form = this.querySelector("form");
    this.formSubmit = this.querySelector("[localization-switch]");
  }

  connectedCallback() {
    super.connectedCallback();

    this.querySelectorAll(".market-switcher__input").forEach((input) => {
      input.addEventListener("change", (e) => {
        const group = e.target.closest(".market-switcher__group");
        const field = group?.dataset.field;
        const displayValue = e.target.dataset.value;

        if (field) this.form[field].value = e.target.value;

        if (displayValue && group) {
          group.querySelector(".market-switcher__current").textContent = displayValue;
        }

        if (group) group.removeAttribute("open");
      });
    });

    this.formSubmit.addEventListener("click", (e) => {
      e.preventDefault();
      this.formSubmit.toggleAttribute("is-loading");
      this.form.submit();
    });
  }
}

if (!customElements.get("market-switcher"))
  customElements.define("market-switcher", MarketSwitcher);

// Footer collapsible element overrides
document.addEventListener('click', (e) => {
  const toggle = e.target.closest('.footer--collapsible-menu .collapsible-row--toggle[href]');
  if (!toggle) return;
  if (toggle.getAttribute('aria-expanded') === 'true' || toggle.dataset.noChildren === 'true') {
    e.stopImmediatePropagation();
    window.location.href = toggle.href;
  } else {
    e.preventDefault();
  }
}, true);

// Disclosure current option display fix
// document.addEventListener('click', (e) => {
//   const option = e.target.closest('.disclosure--option');
//   if (!option) return;
//   const tmpl = option.querySelector('template.disclosure-option--current-display');
//   if (!tmpl) return;
//   const disclosure = option.closest('disclosure-element');
//   if (!disclosure) return;
//   const currentOption = disclosure.querySelector('.disclosure--current-option');
//   if (!currentOption) return;
//   setTimeout(() => {
//     currentOption.innerHTML = tmpl.innerHTML;
//   }, 0);
// });

// Newsletter agreement validation
function _checkSubscribeAgreement(el) {
  if (!el.hasAttribute('data-agreement-required')) return true;
  const checkbox = el.querySelector('.subscribe-form--agreement-checkbox');
  const form = el.querySelector('form');
  if (checkbox && !checkbox.checked) {
    window.dispatchEvent(new CustomEvent('theme:feedback:load', { detail: { async: 'agreement-required' } }));
    if (form) { form.classList.add('form--error'); form.classList.remove('form--success'); }
    return false;
  }
  if (form) form.classList.remove('form--error');
  return true;
}

// Layer 1: intercept click before SubscribeForm's own listener
document.addEventListener('click', (e) => {
  const submitBtn = e.target.closest('.subscribe-form--submit');
  if (!submitBtn) return;
  const root = submitBtn.closest('subscribe-form[data-agreement-required]');
  if (!root) return;
  if (!_checkSubscribeAgreement(root)) {
    e.preventDefault();
    e.stopImmediatePropagation();
  }
}, true);

// Layer 2: patch SubscribeForm.submitForm so hCaptcha callbacks can't bypass layer 1
customElements.whenDefined('subscribe-form').then(() => {
  const Cls = customElements.get('subscribe-form');
  if (!Cls) return;
  const _orig = Cls.prototype.submitForm;
  Cls.prototype.submitForm = function () {
    if (!_checkSubscribeAgreement(this)) return;
    _orig.call(this);
  };
});

// Clear agreement error when checkbox is checked
document.addEventListener('change', (e) => {
  const checkbox = e.target.closest('.subscribe-form--agreement-checkbox');
  if (!checkbox || !checkbox.checked) return;
  const form = checkbox.closest('form');
  if (form) form.classList.remove('form--error');
});

// Clear success message when user starts typing a new email
document.addEventListener('input', (e) => {
  const input = e.target.closest('.subscribe-form--input');
  if (!input) return;
  const form = input.closest('form');
  if (form) form.classList.remove('form--success');
});

const apply = (btn) => { 
  const active = btn.getAttribute('data-mib-wishlist-active') === 'true'; 
  btn.setAttribute('aria-label', active ? btn.getAttribute('data-t-added-to-wishlist') : btn.getAttribute('data-t-add-to-wishlist')); 
};

document.querySelectorAll('.mib-global-wishlist-btn').forEach(apply);

const obs = new MutationObserver((m) => m.forEach((x) => { const el = x.target; if (el instanceof HTMLElement && el.classList.contains('mib-global-wishlist-btn')) { apply(el); } })); obs.observe(document.documentElement, { subtree: true, attributes: true, attributeFilter: ['data-mib-wishlist-active', 'data-mib-wishlist-count', 'data-mib-wishlist-loading'] });

// Predictive search — Swiper product grid
(function () {
  function initPredictiveSearchSwiper() {
    const outer = document.querySelector('.predictive-search--swiper-outer');
    if (!outer) return;
    const el = outer.querySelector('.predictive-search--product-grid');
    if (!el || !el.querySelector('.swiper-slide')) return;

    if (el._psSwiper) {
      el._psSwiper.destroy(true, true);
      el._psSwiper = null;
    }

    el._psSwiper = new Swiper(el, {
      slidesPerView: 2.2,
      spaceBetween: 12,
      navigation: {
        prevEl: outer.querySelector('.predictive-search--swiper-prev'),
        nextEl: outer.querySelector('.predictive-search--swiper-next'),
      },
      breakpoints: {
        640: {
          slidesPerView: 2.5
        },
        991: {
          slidesPerView: 3.5
        },
        1200: {
          slidesPerView: 4.5
        }
      }
    });
  }

  function tryInit(attempts) {
    if (typeof Swiper !== 'undefined') {
      initPredictiveSearchSwiper();
    } else if (attempts > 0) {
      setTimeout(() => tryInit(attempts - 1), 100);
    }
  }

  window.addEventListener('theme:search:reloaded', () => tryInit(20));

  // Init for default/promoted products shown on first render (no AJAX event fired)
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => tryInit(20));
  } else {
    tryInit(20);
  }
})();

// Collection tabs - drag to scroll
(function() {
  function initDragScroll() {
    const scrollContainers = document.querySelectorAll('.collection-tabs--scroll');
    
    scrollContainers.forEach(container => {
      let isDown = false;
      let startX;
      let scrollLeft;
      let hasMoved = false;
      
      function checkScrollable() {
        const isScrollable = container.scrollWidth > container.clientWidth;
        container.dataset.dragEnabled = isScrollable ? '1' : '0';
        return isScrollable;
      }
      
      checkScrollable();
      
      const resizeObserver = new ResizeObserver(() => checkScrollable());
      resizeObserver.observe(container);
      
      const links = container.querySelectorAll('a');
      links.forEach(link => {
        link.addEventListener('dragstart', e => e.preventDefault());
      });
      
      container.addEventListener('mousedown', e => {
        if (!checkScrollable()) return;
        isDown = true;
        hasMoved = false;
        startX = e.pageX - container.offsetLeft;
        scrollLeft = container.scrollLeft;
        container.style.scrollSnapType = 'none';
      });
      
      container.addEventListener('mouseleave', () => {
        isDown = false;
        container.style.scrollSnapType = '';
      });
      
      container.addEventListener('mouseup', () => {
        isDown = false;
        container.style.scrollSnapType = '';
        
        if (hasMoved) {
          setTimeout(() => { hasMoved = false; }, 10);
        }
      });
      
      container.addEventListener('mousemove', e => {
        if (!isDown) return;
        e.preventDefault();
        const x = e.pageX - container.offsetLeft;
        const walk = (x - startX);
        
        if (Math.abs(walk) > 5) {
          hasMoved = true;
        }
        
        container.scrollLeft = scrollLeft - walk;
      });
      
      container.addEventListener('click', e => {
        if (hasMoved) {
          e.preventDefault();
          e.stopPropagation();
        }
      }, true);
    });
  }
  
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initDragScroll);
  } else {
    initDragScroll();
  }
})();

// Responsive video - switch sources on breakpoint
(function() {
  const MOBILE_BREAKPOINT = 768;
  
  function normalizeUrl(url) {
    // Convert relative URL to absolute for comparison
    if (!url) return '';
    if (url.startsWith('http://') || url.startsWith('https://')) return url;
    return new URL(url, window.location.origin).href;
  }
  
  function initResponsiveVideos() {
    const videos = document.querySelectorAll('video[data-responsive-video]');
    if (!videos.length) return;
    
    videos.forEach(video => {
      let currentMode = null;
      const mobileSrc = video.dataset.srcMobile;
      const desktopSrc = video.dataset.srcDesktop;
      
      if (!mobileSrc || !desktopSrc) return;
      
      function updateSource() {
        const isMobile = window.innerWidth < MOBILE_BREAKPOINT;
        const newMode = isMobile ? 'mobile' : 'desktop';
        
        // Only update if mode changed
        if (currentMode === newMode) return;
        currentMode = newMode;
        
        const newSrc = isMobile ? mobileSrc : desktopSrc;
        const sourceEl = video.querySelector('source');
        
        if (sourceEl) {
          const currentSrc = normalizeUrl(sourceEl.getAttribute('src'));
          const targetSrc = normalizeUrl(newSrc);
          
          if (currentSrc !== targetSrc) {
            const wasPlaying = !video.paused;
            const currentTime = video.currentTime;
            
            sourceEl.setAttribute('src', newSrc);
            video.load();
            
            if (wasPlaying) {
              video.currentTime = currentTime;
              video.play().catch(() => {});
            }
          }
        }
      }
      
      updateSource();
      
      let resizeTimeout;
      window.addEventListener('resize', () => {
        clearTimeout(resizeTimeout);
        resizeTimeout = setTimeout(updateSource, 250);
      });
    });
  }
  
  // Initialize on load
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initResponsiveVideos);
  } else {
    initResponsiveVideos();
  }
  
  // Re-initialize when Shopify section is updated (for theme editor)
  if (typeof Shopify !== 'undefined' && Shopify.designMode) {
    document.addEventListener('shopify:section:load', initResponsiveVideos);
    document.addEventListener('shopify:section:reorder', initResponsiveVideos);
  }
})();

// Eveline Featured Collection — Swiper slider
(function () {
  function initEvelineFeaturedCollectionSwiper() {
    const sections = document.querySelectorAll('.eveline-featured-col__products');
    if (!sections.length) return;

    sections.forEach((outer) => {
      const el = outer.querySelector('.eveline-featured-col__slider');
      if (!el || !el.querySelector('.swiper-slide')) return;

      if (el._efcSwiper) {
        el._efcSwiper.destroy(true, true);
        el._efcSwiper = null;
      }

      // Find all navigation buttons - both desktop and mobile
      const wrapper = outer.closest('.eveline-featured-col__wrapper');
      const allNavs = wrapper ? wrapper.querySelectorAll('.eveline-featured-col__nav') : [];
      
      // Collect all prev and next buttons from all navigations
      const prevButtons = [];
      const nextButtons = [];
      allNavs.forEach(nav => {
        const prevBtn = nav.querySelector('.eveline-featured-col__btn-prev');
        const nextBtn = nav.querySelector('.eveline-featured-col__btn-next');
        if (prevBtn) prevButtons.push(prevBtn);
        if (nextBtn) nextButtons.push(nextBtn);
      });
      
      // Check if wrapper has TikTok
      const hasTikTok = wrapper && wrapper.classList.contains('eveline-featured-col__wrapper--with-tiktok');
      
      // Separate configurations for TikTok and standard layout
      if (hasTikTok) {
        el._efcSwiper = new Swiper(el, {
          spaceBetween: 24,
          slidesPerView: 1.5,
          centeredSlides: false,
          slideToClickedSlide: false,
          watchSlidesProgress: true,
          navigation: (prevButtons.length > 0 || nextButtons.length > 0) ? {
            prevEl: prevButtons,
            nextEl: nextButtons,
          } : false,
          breakpoints: {
            540: {
              slidesPerView: 2.5,
              spaceBetween: 24
            },
            768: {
              slidesPerView: 1,
              spaceBetween: 24
            },
            992: {
              slidesPerView: 2,
              spaceBetween: 24
            },
            1400: {
              slidesPerView: 3,
              spaceBetween: 32
            }
          }
        });
      } else {
        el._efcSwiper = new Swiper(el, {
          spaceBetween: 24,
          slidesPerView: 1.5,
          centeredSlides: false,
          slideToClickedSlide: false,
          watchSlidesProgress: true,
          navigation: (prevButtons.length > 0 || nextButtons.length > 0) ? {
            prevEl: prevButtons,
            nextEl: nextButtons,
          } : false,
          breakpoints: {
            540: {
              slidesPerView: 2.5,
              spaceBetween: 24
            },
            992: {
              slidesPerView: 3.5,
              spaceBetween: 24
            },
            1400: {
              slidesPerView: 4.5,
              spaceBetween: 32
            },
            1800: {
              slidesPerView: 4.5,
              spaceBetween: 32
            }
          }
        });
      }
    });
  }

  function tryInit(attempts) {
    if (typeof Swiper !== 'undefined') {
      initEvelineFeaturedCollectionSwiper();
    } else if (attempts > 0) {
      setTimeout(() => tryInit(attempts - 1), 100);
    }
  }

  // Init on page load
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => tryInit(20));
  } else {
    tryInit(20);
  }

  // Re-init on Shopify section events (theme editor)
  document.addEventListener('shopify:section:load', (event) => {
    if (event.target.querySelector('.eveline-featured-col__slider')) {
      setTimeout(() => tryInit(20), 100);
    }
  });

  document.addEventListener('shopify:section:reorder', () => {
    setTimeout(() => tryInit(20), 100);
  });
})();

// Pause slideshow auto-rotate while a video slide is active.
// The slideshow-carousel has pauseAutoRotate() / resumeAutoRotate() methods.
// We watch data-active changes on blocks via MutationObserver.
(function () {
  function hasVideoBlock(block) {
    return !!block.querySelector('video-component');
  }

  function checkAndToggleRotate(carousel) {
    var isMobile = window.matchMedia('(max-width:767px)').matches;
    var attr = isMobile ? 'data-mobile-active' : 'data-active';
    var activeBlocks = Array.prototype.slice.call(
      carousel.querySelectorAll('.carousel--block[' + attr + '="true"]')
    );
    var videoIsActive = activeBlocks.some(hasVideoBlock);

    if (videoIsActive) {
      if (typeof carousel.pauseAutoRotate === 'function') carousel.pauseAutoRotate();
    } else {
      if (typeof carousel.resumeAutoRotate === 'function') carousel.resumeAutoRotate();
    }
  }

  function initCarousel(carousel) {
    var blocks = carousel.querySelectorAll('.carousel--block');
    if (!blocks.length) return;

    var observer = new MutationObserver(function () {
      checkAndToggleRotate(carousel);
    });

    blocks.forEach(function (block) {
      observer.observe(block, {
        attributes: true,
        attributeFilter: ['data-active', 'data-mobile-active']
      });
    });

    // Check initial state after carousel has had time to start its timer
    setTimeout(function () { checkAndToggleRotate(carousel); }, 300);
  }

  function init() {
    document.querySelectorAll('slideshow-carousel').forEach(initCarousel);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);
})();

// ── Cart recommendations ──
(function () {
  function initSwiper(container) {
    var el = container.querySelector('.cart--rec-swiper');
    if (!el) return;
    if (el._recSwiper) { el._recSwiper.destroy(true, true); el._recSwiper = null; }
    var isPage = container.dataset.view && container.dataset.view !== 'drawer';
    el._recSwiper = new Swiper(el, {
      slidesPerView: isPage ? 2.5 : 1.6,
      grabCursor: true,
      spaceBetween: 24,
      slidesOffsetBefore: 24,
      slidesOffsetAfter: 24,
      breakpoints: isPage ? {
        640: { slidesPerView: 3.2 },
        1024: { slidesPerView: 4.2 },
      } : {
        640: { slidesPerView: 2.2 },
      }
    });
    if (!el._swipeBlocked) {
      el._swipeBlocked = true;
      el.addEventListener('touchstart', function () {
        var blockSwipe = function (ev) { ev.stopImmediatePropagation(); };
        document.addEventListener('swipe', blockSwipe, { capture: true });
        var cleanup = function () {
          document.removeEventListener('swipe', blockSwipe, { capture: true });
        };
        document.addEventListener('touchend', cleanup, { once: true, passive: true });
        document.addEventListener('touchcancel', cleanup, { once: true, passive: true });
      }, { passive: true });
    }
  }

  function tryInitSwiper(container, attempts) {
    if (typeof Swiper !== 'undefined') {
      initSwiper(container);
    } else if (attempts > 0) {
      setTimeout(function () { tryInitSwiper(container, attempts - 1); }, 100);
    }
  }

  function initCartRecommendations(root) {
    var container = (root || document).querySelector('.cart--recommendations');
    if (!container || container.dataset.loaded) return;

    var productId = container.dataset.cartProductId;
    var tHeading = container.dataset.tHeading || 'Recommended';

    if (!productId) return;
    container.dataset.loaded = 'true';

    var routesRoot = (typeof Shopify !== 'undefined' && Shopify.routes && Shopify.routes.root)
      ? Shopify.routes.root.replace(/\/$/, '')
      : '';

    fetch(routesRoot + '/recommendations/products?product_id=' + productId + '&limit=6&intent=related&section_id=cart-recommendations')
      .then(function (r) { return r.text(); })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var inner = doc.querySelector('.cart--recommendations-inner');
        if (!inner || !inner.children.length) return;

        container.innerHTML =
          '<h3 class="cart--recommendations-heading" data-item="nav-text">' + tHeading + '</h3>' +
          '<div class="cart--recommendations-grid">' + inner.innerHTML + '</div>';

        tryInitSwiper(container, 30);
      })
      .catch(function () {});
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initCartRecommendations(); });
  } else {
    initCartRecommendations();
  }

  window.addEventListener('theme:cart:drawer:updateHtml', function () {
    setTimeout(function () { initCartRecommendations(); }, 50);
  });
  window.addEventListener('theme:cart:mobile:updateHtml', function () {
    setTimeout(function () { initCartRecommendations(); }, 50);
  });
})();

class CartUpsellElement extends HTMLElement {
  connectedCallback() {
    this._productId = Number(this.dataset.productId);
    this._variants = [];
    this._selectedOptions = [];
    this._listeners = [];
    this._init();
    this._bindCartEvents();
  }

  disconnectedCallback() {
    for (var i = 0; i < this._listeners.length; i++) {
      this._listeners[i][0].removeEventListener(this._listeners[i][1], this._listeners[i][2]);
    }
  }

  _init() {
    var script = this.querySelector('[data-upsell-variants]');
    if (script) {
      try { this._variants = JSON.parse(script.textContent); } catch (e) {}
    }

    // Read initial selected options from switch-list HTML
    var self = this;
    this.querySelectorAll('[data-upsell-option]').forEach(function (optEl) {
      var selected = optEl.querySelector('.switch-list-option.is-selected');
      self._selectedOptions[Number(optEl.dataset.upsellOption)] = selected ? selected.dataset.optionValue : '';
    });

    // Switch-list bar toggle
    this.querySelectorAll('.switch-list-bar').forEach(function (bar) {
      var clickHandler = function (e) {
        e.stopPropagation();
        var list = bar.closest('.switch-list');
        var isOpen = list.classList.contains('is-open');
        self.querySelectorAll('.switch-list.is-open').forEach(function (l) {
          l.classList.remove('is-open');
          var b = l.querySelector('.switch-list-bar');
          if (b) b.setAttribute('aria-expanded', 'false');
        });
        if (!isOpen) {
          list.classList.add('is-open');
          bar.setAttribute('aria-expanded', 'true');
        }
      };
      var keyHandler = function (e) {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); clickHandler(e); }
        if (e.key === 'Escape') {
          bar.closest('.switch-list').classList.remove('is-open');
          bar.setAttribute('aria-expanded', 'false');
        }
      };
      bar.addEventListener('click', clickHandler);
      bar.addEventListener('keydown', keyHandler);
      self._listeners.push([bar, 'click', clickHandler], [bar, 'keydown', keyHandler]);
    });

    // Switch-list option select
    this.querySelectorAll('.switch-list-option').forEach(function (opt) {
      var handler = function () {
        var optIdx = Number(opt.dataset.optionIdx);
        var val = opt.dataset.optionValue;
        var list = opt.closest('.switch-list');

        list.querySelectorAll('.switch-list-option').forEach(function (o) {
          o.classList.remove('is-selected');
          o.setAttribute('aria-selected', 'false');
        });
        opt.classList.add('is-selected');
        opt.setAttribute('aria-selected', 'true');

        var bar = list.querySelector('.switch-list-bar');
        if (bar) {
          bar.querySelector('.switch-list-bar__name').textContent = val;
          var optImg = opt.querySelector('.switch-list-option__image');
          var barImg = bar.querySelector('.switch-list-bar__image');
          if (barImg && optImg) barImg.style.cssText = optImg.style.cssText;
        }

        list.classList.remove('is-open');
        if (bar) bar.setAttribute('aria-expanded', 'false');

        self._selectedOptions[optIdx] = val;
        self._syncVariant();
      };
      var keyHandler = function (e) {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handler(); }
      };
      opt.addEventListener('click', handler);
      opt.addEventListener('keydown', keyHandler);
      self._listeners.push([opt, 'click', handler], [opt, 'keydown', keyHandler]);
    });

    // Close on outside click
    var outsideHandler = function (e) {
      if (!self.contains(e.target)) {
        self.querySelectorAll('.switch-list.is-open').forEach(function (l) {
          l.classList.remove('is-open');
          var b = l.querySelector('.switch-list-bar');
          if (b) b.setAttribute('aria-expanded', 'false');
        });
      }
    };
    document.addEventListener('click', outsideHandler);
    this._listeners.push([document, 'click', outsideHandler]);

    // ATC button
    var btn = this.querySelector('[data-upsell-atc]');
    if (btn) {
      var atcHandler = function () { self._addToCart(); };
      btn.addEventListener('click', atcHandler);
      this._listeners.push([btn, 'click', atcHandler]);
    }
  }

  _syncVariant() {
    var variant = this._findVariant();
    var btn = this.querySelector('[data-upsell-atc]');
    var priceEl = this.querySelector('[data-upsell-price]');

    if (variant) {
      if (btn) {
        btn.dataset.variantId = variant.id;
        btn.disabled = !variant.available;
      }
      if (priceEl) {
        priceEl.innerHTML = variant.compare_at_price > variant.price
          ? '<strong class="evgs__gift-price">' + this._esc(variant.price_fmt) + '</strong> <s class="evgs__orig-price">' + this._esc(variant.compare_fmt) + '</s>'
          : '<strong class="evgs__gift-price">' + this._esc(variant.price_fmt) + '</strong>';
      }
    } else {
      if (btn) btn.disabled = true;
    }
  }

  _findVariant() {
    var opts = this._selectedOptions;
    return this._variants.find(function (v) {
      return v.options.every(function (opt, i) {
        return !opts[i] || opt === opts[i];
      });
    });
  }

  _addToCart() {
    var self = this;
    var btn = this.querySelector('[data-upsell-atc]');
    var variantId = btn ? Number(btn.dataset.variantId) : 0;
    if (!variantId || !btn) return;

    btn.setAttribute('aria-busy', 'true');
    btn.disabled = true;

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        items: [{ id: variantId, quantity: 1 }],
        sections: ['cart-drawer'],
      }),
    })
      .then(function (res) {
        if (!res.ok) throw new Error('cart/add failed');
        return res.json();
      })
      .then(function (data) {
        var sections = data.sections || {};
        // Notify other components immediately — don't wait for theme:cart:updated
        document.dispatchEvent(new CustomEvent('eveline:cart:updated', { bubbles: true }));
        if (typeof window.trigger === 'function' && sections['cart-drawer']) {
          window.addEventListener('theme:cart:updated', function () {
            requestAnimationFrame(function () {
              window.trigger('theme:transition:reset:cart-drawer');
              window.trigger('theme:drawer:open', { side: 'right', view: 'cart-drawer', trigger: null });
            });
          }, { once: true });
          window.trigger('theme:cart:drawer:updateHtml', {
            section: sections['cart-drawer'],
            load_transitions: true,
          });
        }
      })
      .catch(function () {
        btn.setAttribute('aria-busy', 'false');
        btn.disabled = false;
      });
  }

  _bindCartEvents() {
    var self = this;
    var refresh = function () { self._fetchCart(); };
    document.addEventListener('eveline:cart:updated', refresh);
    window.addEventListener('theme:cart:updated', refresh);
    window.addEventListener('theme:cart:productAdded', refresh);
    this._listeners.push(
      [document, 'eveline:cart:updated', refresh],
      [window, 'theme:cart:updated', refresh],
      [window, 'theme:cart:productAdded', refresh]
    );
  }

  _fetchCart() {
    var self = this;
    if (this._fetchAbort) this._fetchAbort.abort();
    this._fetchAbort = new AbortController();
    fetch('/cart.js', { signal: this._fetchAbort.signal })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        self._fetchAbort = null;
        var inCart = (cart.items || []).some(function (item) {
          return item.product_id === self._productId;
        });
        if (!inCart) {
          var btn = self.querySelector('[data-upsell-atc]');
          if (btn) {
            btn.setAttribute('aria-busy', 'false');
            btn.disabled = false;
          }
        }
        self.classList.toggle('is-hidden', inCart);
      })
      .catch(function () {});
  }

  _esc(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }
}

if (!customElements.get('cart-upsell-element')) {
  customElements.define('cart-upsell-element', CartUpsellElement);
}

// Update .collection-title__value — triggered by URL change (history.replaceState)
(function () {
  const currentSpan = document.querySelector('.collection-title__value span');
  if (!currentSpan || !location.pathname.includes('/collections/')) return;

  // Intercept history.replaceState to detect URL changes made by theme.js
  const originalReplaceState = history.replaceState.bind(history);
  let abortController = null;

  history.replaceState = function (state, title, url) {
    originalReplaceState(state, title, url);
    if (!url || !String(url).includes('/collections/')) return;

    // Fire fetch in parallel with theme's own AJAX
    if (abortController) abortController.abort();
    abortController = new AbortController();
    fetch(url, { signal: abortController.signal })
      .then(res => res.text())
      .then(html => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const newSpan = doc.querySelector('.collection-title__value span');
        if (newSpan) currentSpan.textContent = newSpan.textContent;
      })
      .catch(() => {});
  };
})();

// Filter drawer — sync aria-hidden on menus with aria-expanded state of toggles
(function () {
  function syncFilterMenu(toggle) {
    const menuId = toggle.getAttribute('aria-controls');
    if (!menuId) return;
    const menu = document.getElementById(menuId);
    if (!menu) return;
    menu.setAttribute('aria-hidden', toggle.getAttribute('aria-expanded') !== 'true' ? 'true' : 'false');
  }

  function initFilterAria() {
    const toggles = document.querySelectorAll('.filter--toggle[aria-controls]');
    toggles.forEach(syncFilterMenu);

    const observer = new MutationObserver(mutations => {
      mutations.forEach(m => {
        if (m.attributeName === 'aria-expanded') syncFilterMenu(m.target);
      });
    });
    toggles.forEach(toggle => observer.observe(toggle, { attributes: true, attributeFilter: ['aria-expanded'] }));
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initFilterAria);
  } else {
    initFilterAria();
  }
})();

// Filter value search — filters list items inside .filter--menu as user types
(function () {
  document.addEventListener('input', function (e) {
    const input = e.target;
    if (!input.classList.contains('filter--value-search-input')) return;
    const menu = input.closest('.filter--menu');
    if (!menu) return;
    const query = input.value.trim().toLowerCase();
    menu.querySelectorAll('.filter--link').forEach(function (link) {
      const text = (link.querySelector('span') || link).textContent.toLowerCase();
      link.style.display = (!query || text.includes(query)) ? '' : 'none';
    });
  });
})();

// Search input: submit form on Enter key
document.addEventListener('keydown', function (e) {
  if (e.key !== 'Enter') return;
  const input = e.target.closest('#search_input');
  if (!input) return;
  e.preventDefault();
  const form = input.closest('form');
  if (form) form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
});

// Update .product-buy-buttons--drawer-variant on variant change
document.addEventListener('option-change', function () {
  setTimeout(function () {
    const drawerVariant = document.querySelector('.product-buy-buttons--drawer-variant');
    if (!drawerVariant) return;
    const titles = Array.from(document.querySelectorAll('.switchers-title__value'))
      .map(el => el.textContent.trim()).filter(Boolean);
    if (titles.length) drawerVariant.textContent = titles.join(' / ');
  }, 100);
});

// Move the `selected` class on .product-unit-price to the active variant on change.
// The variant can change via the native <product-options-element> or the Eveline
// gallery/color switcher. Both fire `theme:product:<productId>:variantChanged` on
// window (with detail.variant), so we bind to that — `option-change` alone misses
// the native theme switcher. As a fallback we also react to the `change` event the
// Eveline gallery dispatches on `.product-buy-buttons--input`.
(function () {
  function applySelected(scope, variantId) {
    if (!variantId) return;
    (scope || document).querySelectorAll('.product-unit-price').forEach(function (el) {
      el.classList.toggle('selected', el.getAttribute('data-variant-detail') === String(variantId));
    });
  }

  function bind(productId) {
    window.addEventListener('theme:product:' + productId + ':variantChanged', function (e) {
      var variant = e.detail && e.detail.variant;
      if (!variant || !variant.id) return;
      var anchor = document.querySelector('product-buy-buttons-element[data-id="' + productId + '"]')
        || document.querySelector('product-options-element[data-id="' + productId + '"]');
      var scope = anchor ? anchor.closest('.product-form') : null;
      applySelected(scope, variant.id);
    });
  }

  function init() {
    var ids = {};
    document
      .querySelectorAll('product-buy-buttons-element[data-id], product-options-element[data-id]')
      .forEach(function (el) {
        var id = el.getAttribute('data-id');
        if (id && !ids[id]) { ids[id] = true; bind(id); }
      });

    // Fallback: Eveline gallery sets the input value and dispatches a bubbling change.
    document.addEventListener('change', function (e) {
      var input = e.target;
      if (!input || !input.classList || !input.classList.contains('product-buy-buttons--input')) return;
      applySelected(input.closest('.product-form'), input.value);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();

(function () {
  function fixStickyTop() {
    const table = document.querySelector('table.shipping-eu');
    if (!table) return;
    const firstRow = table.querySelector('thead tr:first-child');
    const secondRowCells = table.querySelectorAll('thead tr:nth-child(2) th');
    if (!firstRow || !secondRowCells.length) return;
    const h = firstRow.getBoundingClientRect().height;
    secondRowCells.forEach(th => th.style.top = h + 'px');
  }

  document.addEventListener('DOMContentLoaded', fixStickyTop);
  window.addEventListener('resize', fixStickyTop);
  fixStickyTop();
})();

// Fix: Handle cart/change.js 422 errors
(function () {
  function patchCartPrototype() {
    var CartClass = customElements.get('cart-element');
    if (!CartClass) return false;
    if (CartClass.prototype._evelineQtyPatched) return true;
    CartClass.prototype._evelineQtyPatched = true;

    var origUpdateQty = CartClass.prototype.updateQuantity;
    CartClass.prototype.updateQuantity = async function (line, qty) {
      try {
        return await origUpdateQty.call(this, line, qty);
      } catch (err) {
        // theme.js throws on 422 — re-enable checkout and refresh cart HTML
        if (this.checkout_button) {
          this.checkout_button.removeAttribute('disabled');
        }
        // Re-fetch current cart state so the input reflects what Shopify actually set
        try {
          var sections = window.Cart && Cart.sections ? Cart.sections : ['cart-drawer'];
          var resp = await fetch(
            Shopify.routes.cart + '?sections=' + sections.join(',')
          );
          if (resp.ok) {
            var data = await resp.json();
            if (data.sections) {
              Cart.updateAllHtml(data.sections);
            }
          }
        } catch (_) {}
        return false;
      }
    };
    return true;
  }

  if (!patchCartPrototype()) {
    document.addEventListener('DOMContentLoaded', function () {
      if (!patchCartPrototype()) {
        window.addEventListener('load', patchCartPrototype);
      }
    });
  }
})();

// Quantity selector max enforcement
(function () {

  function getContainer(qs) {
    return qs.querySelector('.quantity-selector--container');
  }

  function getEffectiveMax(container, type) {
    var max = parseInt(container.getAttribute('data-current-variant-qty-max'));
    if (isNaN(max)) return null;
    if (type === 'product') {
      var inCart = parseInt(container.getAttribute('data-current-variant-qty-in-cart')) || 0;
      return Math.max(1, max - inCart);
    }
    // cart: max is the inventory cap; input value IS the current qty
    return max;
  }

  function showFeedbackBar(maxQty) {
    var msg = window.eveline && window.eveline.t && window.eveline.t.product && window.eveline.t.product.max_items_you_can_add;
    if (!msg || !maxQty) return;
    msg = msg + ' ' + maxQty;
    var bar = document.querySelector('feedback-bar-element');
    if (!bar) return;
    if (!bar.message_json) bar.message_json = {};
    
    // Use unique key with timestamp to force showing message each time
    var key = 'qty-max-' + Date.now();
    bar.message_json[key] = msg;
    if (typeof bar.load === 'function') bar.load(key);
  }

  function syncState(qs) {
    var container = getContainer(qs);
    if (!container) return;
    var input = qs.querySelector('.quantity-selector--input');
    var plus  = qs.querySelector('.quantity-selector--plus');
    if (!input || !plus) return;

    var type  = qs.getAttribute('data-type');
    var max   = getEffectiveMax(container, type);
    if (max === null) return;

    input.setAttribute('max', max);
    var val   = parseInt(input.value) || 0;
    var atMax = val >= max;

    // Toggle class on container for product forms when qty_in_cart >= qty_max
    if (type === 'product') {
      var qtyMax = parseInt(container.getAttribute('data-current-variant-qty-max'));
      var inCart = parseInt(container.getAttribute('data-current-variant-qty-in-cart')) || 0;
      var shouldDisable = inCart >= qtyMax;
      container.classList.toggle('quantity-selector--container--add-disabled', shouldDisable);
    }

    plus.classList.toggle('quantity-selector--plus-disabled', atMax);
    if (atMax) {
      plus.setAttribute('disabled', 'disabled');
      plus.setAttribute('aria-disabled', 'true');
    } else {
      plus.removeAttribute('disabled');
      plus.setAttribute('aria-disabled', 'false');
    }
  }

  // Block ATC button when .quantity-selector--container--add-disabled is present
  function syncAtcButton(form) {
    if (!form) return;
    var container = form.querySelector('.quantity-selector--container');
    var atcBtn = form.querySelector('.product-buy-buttons--primary');
    if (!container || !atcBtn) return;
    
    var shouldDisable = container.classList.contains('quantity-selector--container--add-disabled');
    atcBtn.toggleAttribute('disabled', shouldDisable);
    atcBtn.setAttribute('aria-disabled', shouldDisable ? 'true' : 'false');

    // Update button text
    var ctaText = atcBtn.querySelector('.product-buy-buttons--cta-text');
    if (ctaText) {
      if (shouldDisable) {
        // Store original text if not already stored
        if (!atcBtn.hasAttribute('data-default-text')) {
          var originalText = atcBtn.getAttribute('data-text') || ctaText.textContent.trim();
          atcBtn.setAttribute('data-default-text', originalText);
        }
        // Set max message text
        var maxBtnText = (window.eveline && window.eveline.t && window.eveline.t.product && window.eveline.t.product.max_message_button) || 'Maximum quantity in cart';
        ctaText.textContent = maxBtnText;
      } else {
        // Restore original text
        var defaultText = atcBtn.getAttribute('data-default-text') || atcBtn.getAttribute('data-text');
        if (defaultText) {
          ctaText.textContent = defaultText;
        }
      }
    }
  }

  // Watch for class changes on containers
  var atcObserver = new MutationObserver(function (mutations) {
    mutations.forEach(function (m) {
      if (m.type === 'attributes' && m.attributeName === 'class') {
        var container = m.target;
        var form = container.closest('form');
        if (form) {
          syncAtcButton(form);
        }
      }
      // Also watch for data-current-variant-qty-in-cart changes
      if (m.type === 'attributes' && m.attributeName === 'data-current-variant-qty-in-cart') {
        var container = m.target;
        var qs = container.closest('quantity-selector');
        if (qs) {
          syncState(qs);
          var form = container.closest('form');
          if (form) syncAtcButton(form);
        }
      }
    });
  });

  function observeContainer(qs) {
    var container = getContainer(qs);
    if (container && qs.getAttribute('data-type') === 'product') {
      atcObserver.observe(container, { 
        attributes: true, 
        attributeFilter: ['class', 'data-current-variant-qty-in-cart'] 
      });
      syncAtcButton(container.closest('form'));
    }
  }

  function patchQtySelector(qs) {
    if (qs.dataset.evelineQtyPatched) return;
    var container = getContainer(qs);
    if (!container) return;
    var input = qs.querySelector('.quantity-selector--input');
    var plus  = qs.querySelector('.quantity-selector--plus');
    var minus = qs.querySelector('.quantity-selector--minus');
    if (!input || !plus) return;

    qs.dataset.evelineQtyPatched = '1';

    // For product form: initialize data-current-variant-qty-in-cart from Cart.basket on init
    if (qs.getAttribute('data-type') === 'product') {
      var form = qs.closest('form');
      var buyInput = form && form.querySelector('.product-buy-buttons--input');
      if (buyInput) {
        var variantId = parseInt(buyInput.getAttribute('data-id') || buyInput.value);
        if (variantId && window.Cart && Cart.basket) {
          var inCart = Cart.basket[variantId] || 0;
          container.setAttribute('data-current-variant-qty-in-cart', inCart);
        }
      }
    }

    syncState(qs);
    observeContainer(qs);

    // Observe plus button class changes to show feedback when disabled
    var plusObserver = new MutationObserver(function (mutations) {
      mutations.forEach(function (mutation) {
        if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
          var hasDisabledClass = plus.classList.contains('quantity-selector--plus-disabled');
          // Only show feedback if class was just added (not removed)
          if (hasDisabledClass && !mutation.oldValue.includes('quantity-selector--plus-disabled')) {
            var qtyMax = parseInt(container.getAttribute('data-current-variant-qty-max'));
            if (qtyMax) {
              showFeedbackBar(qtyMax);
            }
          }
        }
      });
    });
    plusObserver.observe(plus, { 
      attributes: true, 
      attributeFilter: ['class'],
      attributeOldValue: true
    });

    // Block + at max (capture phase, runs before theme.js bubbling handler)
    plus.addEventListener('click', function (e) {
      var type = qs.getAttribute('data-type');
      var max  = getEffectiveMax(container, type);
      if (max !== null && (parseInt(input.value) || 0) >= max) {
        e.stopImmediatePropagation();
        e.preventDefault();
      }
    }, true);

    // Clamp on focusout
    input.addEventListener('focusout', function () {
      var type = qs.getAttribute('data-type');
      var max  = getEffectiveMax(container, type);
      if (max !== null) {
        var val = parseInt(input.value);
        if (!isNaN(val) && val > max) {
          input.value = max;
        }
        if (!isNaN(val) && val < 1) input.value = 1;
      }
      syncState(qs);
    }, true);

    // Clamp on paste
    input.addEventListener('paste', function () {
      setTimeout(function () {
        var type = qs.getAttribute('data-type');
        var max  = getEffectiveMax(container, type);
        if (max !== null) {
          var val = parseInt(input.value);
          if (!isNaN(val) && val > max) {
            input.value = max;
          }
        }
        syncState(qs);
      }, 0);
    });

    input.addEventListener('input', function () { syncState(qs); });
    input.addEventListener('change', function () { syncState(qs); });
 
    // Also sync when minus/plus buttons are clicked (after theme.js handles them)
    if (minus) {
      minus.addEventListener('click', function () {
        setTimeout(function () { syncState(qs); }, 50);
      });
    }
    
    // Sync after plus button changes value (non-capture, runs after theme.js)
    plus.addEventListener('click', function () {
      setTimeout(function () { syncState(qs); }, 50);
    });

    // Product form: re-sync on variant change and after add-to-cart
    if (qs.getAttribute('data-type') === 'product') {
      var form        = qs.closest('form');
      var sectionRoot = form && form.closest('[data-section-id]');
      var buyInput    = form && form.querySelector('.product-buy-buttons--input');

      if (sectionRoot) {
        var productId = sectionRoot.getAttribute('data-id') ||
          (sectionRoot.querySelector('[data-id]') ? sectionRoot.querySelector('[data-id]').getAttribute('data-id') : null);

        if (productId) {
          window.addEventListener('theme:product:' + productId + ':variantChanged', function () {
            setTimeout(function () {
              if (buyInput) {
                var inv    = parseInt(buyInput.getAttribute('data-quantity')) || 0;
                var newMax = Math.min(inv, 50);
                container.setAttribute('data-current-variant-qty-max', newMax);
                var variantId = parseInt(buyInput.getAttribute('data-id') || buyInput.value);
                var inCart    = (window.Cart && Cart.basket && Cart.basket[variantId]) || 0;
                container.setAttribute('data-current-variant-qty-in-cart', inCart);
              }
              syncState(qs);
            }, 80);
          });
        }
      }

      var updateInCart = function () {
        // Fetch cart.js directly instead of relying on Cart.basket
        fetch('/cart.js')
          .then(function (r) { return r.json(); })
          .then(function (cart) {
            if (buyInput) {
              var variantId = parseInt(buyInput.getAttribute('data-id') || buyInput.value);
              var inCart = 0;
              if (cart.items) {
                for (var i = 0; i < cart.items.length; i++) {
                  if (cart.items[i].variant_id === variantId) {
                    inCart = cart.items[i].quantity;
                    break;
                  }
                }
              }
              container.setAttribute('data-current-variant-qty-in-cart', inCart);
              syncState(qs);
            }
          })
          .catch(function (err) {
            // Fallback to Cart.basket if fetch fails
            setTimeout(function () {
              if (buyInput && window.Cart && Cart.basket) {
                var variantId = parseInt(buyInput.getAttribute('data-id') || buyInput.value);
                var inCart = Cart.basket[variantId] || 0;
                container.setAttribute('data-current-variant-qty-in-cart', inCart);
                syncState(qs);
              }
            }, 100);
          });
      };

      window.addEventListener('theme:cart:productAdded', updateInCart);
      window.addEventListener('theme:cart:updated', updateInCart);
      window.addEventListener('theme:cart:drawer:updateHtml', updateInCart);
      document.addEventListener('eveline:cart:updated', updateInCart);
    }
  }

  function patchAll() {
    document.querySelectorAll('quantity-selector').forEach(patchQtySelector);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', patchAll);
  } else {
    patchAll();
  }

  // Re-sync product forms after Cart.basket loads (in case it wasn't ready on init)
  setTimeout(function () {
    document.querySelectorAll('quantity-selector[data-type="product"]').forEach(function (qs) {
      var container = getContainer(qs);
      if (!container) return;
      var form = qs.closest('form');
      var buyInput = form && form.querySelector('.product-buy-buttons--input');
      if (buyInput) {
        fetch('/cart.js')
          .then(function (r) { return r.json(); })
          .then(function (cart) {
            var variantId = parseInt(buyInput.getAttribute('data-id') || buyInput.value);
            var inCart = 0;
            if (cart.items && variantId) {
              for (var i = 0; i < cart.items.length; i++) {
                if (cart.items[i].variant_id === variantId) {
                  inCart = cart.items[i].quantity;
                  break;
                }
              }
            }
            container.setAttribute('data-current-variant-qty-in-cart', inCart);
            syncState(qs);
          })
          .catch(function () {});
      }
    });
  }, 500);

  // Global cart update listener for all product forms
  var globalCartUpdate = function () {
    document.querySelectorAll('quantity-selector[data-type="product"]').forEach(function (qs) {
      var container = getContainer(qs);
      if (!container) return;
      var form = qs.closest('form');
      var buyInput = form && form.querySelector('.product-buy-buttons--input');
      if (buyInput) {
        fetch('/cart.js')
          .then(function (r) { return r.json(); })
          .then(function (cart) {
            var variantId = parseInt(buyInput.getAttribute('data-id') || buyInput.value);
            var inCart = 0;
            if (cart.items && variantId) {
              for (var i = 0; i < cart.items.length; i++) {
                if (cart.items[i].variant_id === variantId) {
                  inCart = cart.items[i].quantity;
                  break;
                }
              }
            }
            container.setAttribute('data-current-variant-qty-in-cart', inCart);
            syncState(qs);
          })
          .catch(function () {});
      }
    });
  };

  window.addEventListener('theme:cart:drawer:updateHtml', globalCartUpdate);
  window.addEventListener('theme:cart:mobile:updateHtml', globalCartUpdate);

  function activateScripts(container) {
    container.querySelectorAll('script').forEach(function (oldScript) {
      if (oldScript.type === 'application/json') return;
      var newScript = document.createElement('script');
      if (oldScript.src) {
        if (document.querySelector('script[src="' + oldScript.src + '"]')) return;
        newScript.src = oldScript.src;
      } else {
        newScript.textContent = oldScript.textContent;
      }
      document.head.appendChild(newScript);
    });
  }

  // theme's swapInNewContent only replaces .cart--body and .cart--totals;
  // we patch the remaining conditional blocks that appear/disappear with cart state
  window.addEventListener('theme:cart:drawer:updateHtml', function (ev) {
    var section = ev.detail && ev.detail.section;
    if (!section) return;
    var parsed = section.parse('.cart--form');
    if (!parsed) return;
    requestAnimationFrame(function () {
      var cartEl = document.querySelector('cart-element[data-view="drawer"]');
      if (!cartEl) return;

      function swapBlock(selector, getAnchor, alwaysReplace) {
        var elOld = cartEl.querySelector(selector);
        var elNew = parsed.querySelector(selector);
        if (elOld && elNew) { if (alwaysReplace) elOld.replaceWith(elNew); }
        else if (elOld && !elNew) { elOld.remove(); }
        else if (!elOld && elNew) { var anchor = getAnchor(); if (anchor) anchor.before(elNew); }
      }

      // header: always replace — contains static {{ cart.item_count }} from Liquid
      swapBlock('.cart--header',          function () { return cartEl.querySelector('.cart--body'); }, true);
      // gwp/upsell/recs: only insert when absent or remove when gone — they self-update via cart events
      var gwpWasAbsent = !cartEl.querySelector('.cart--gwp');
      swapBlock('.cart--gwp',             function () { return cartEl.querySelector('.cart--body'); }, false);
      swapBlock('.cart--upsell',          function () { return cartEl.querySelector('.cart--summary'); }, false);
      swapBlock('.cart--recommendations', function () { return cartEl.querySelector('.cart--summary'); }, false);

      // only activate scripts when GWP is inserted for the first time
      // (scripts inside DOMParser-parsed HTML don't execute on DOM insertion)
      if (gwpWasAbsent) {
        var gwpContainer = cartEl.querySelector('.cart--gwp');
        if (gwpContainer) activateScripts(gwpContainer);
      }
    });
  });

  new MutationObserver(function (mutations) {
    mutations.forEach(function (m) {
      m.addedNodes.forEach(function (node) {
        if (node.nodeType !== 1) return;
        if (node.matches && node.matches('quantity-selector')) patchQtySelector(node);
        if (node.querySelectorAll) node.querySelectorAll('quantity-selector').forEach(patchQtySelector);
      });
    });
  }).observe(document.body, { childList: true, subtree: true });

})();

// Collection grid - autofill from page 2
(function () {
  var promoFillGen = 0;
  var promoFillAbort = null;
  var isAppendingFill = false;
  var fillScheduled = false;
  var lastFillUrl = '';

  function buildPage2Url() {
    var url = new URL(window.location.href);
    url.searchParams.set('page', '2');
    return url.toString();
  }

  function runPromoFill(grid) {
    if (window.innerWidth < 768) return;
    var fill = parseInt(grid.dataset.promoFill, 10);
    if (!fill) return;
    var sectionId = grid.getAttribute('data-transition-container');

    if (promoFillAbort) promoFillAbort.abort();
    promoFillAbort = new AbortController();
    var gen = ++promoFillGen;
    var capturedUrl = window.location.href;

    fetch(buildPage2Url(), { signal: promoFillAbort.signal })
      .then(function (r) { return r.ok ? r.text() : null; })
      .then(function (html) {
        if (gen !== promoFillGen || !html) return;
        grid.dataset.promoFillComplete = capturedUrl;
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var nextSection = sectionId ? doc.querySelector('[data-section-id="' + sectionId + '"]') : null;
        var nextGrid = (nextSection || doc).querySelector('[data-body-grid]');
        if (!nextGrid) return;
        if (!(nextSection || doc).querySelector('[data-body-pagination]')) return;
        var interval = parseInt(grid.getAttribute('data-transition-cascade-interval') || '200', 10);
        var page2Cards = Array.from(nextGrid.children).filter(function (el) {
          return el.hasAttribute('data-transition-item');
        });
        var toFetch = page2Cards.length <= fill ? page2Cards.length : fill;
        var cards = page2Cards.slice(0, toFetch);
        isAppendingFill = true;
        cards.forEach(function (card) {
          card.setAttribute('data-promo-fill-card', '1');
          grid.appendChild(card);
        });
        setTimeout(function () { isAppendingFill = false; }, 0);
        requestAnimationFrame(function () {
          cards.forEach(function (card, i) {
            card.style.setProperty('--transition--delay', (i * interval) + 'ms');
            card.setAttribute('data-transition-active', 'true');
          });
        });
        if (page2Cards.length <= fill) {
          var section = sectionId ? document.querySelector('[data-section-id="' + sectionId + '"]') : null;
          var pagination = (section || document).querySelector('[data-body-pagination]');
          if (pagination) pagination.style.display = 'none';
        }
      })
      .catch(function () {});
  }

  function handlePromoFill(grid) {
    if (grid.dataset.promoFillDone) return;
    grid.dataset.promoFillDone = '1';
    runPromoFill(grid);
  }

  function triggerForGrid() {
    var grid = document.querySelector('[data-body-grid][data-promo-fill]');
    if (!grid) return;
    delete grid.dataset.promoFillDone;
    runPromoFill(grid);
  }

  function scheduleTrigger() {
    if (fillScheduled) return;
    fillScheduled = true;
    setTimeout(function () {
      fillScheduled = false;
      triggerForGrid();
    }, 0);
  }

  document.querySelectorAll('[data-body-grid][data-promo-fill]').forEach(handlePromoFill);

  window.addEventListener('theme:navigation:reloaded', scheduleTrigger);

  new MutationObserver(function (mutations) {
    if (isAppendingFill) return;
    mutations.forEach(function (m) {
      if (!m.addedNodes.length) return;
      m.addedNodes.forEach(function (node) {
        if (node.nodeType !== 1) return;
        if (node.matches('[data-body-grid][data-promo-fill]')) handlePromoFill(node);
        if (node.querySelectorAll) node.querySelectorAll('[data-body-grid][data-promo-fill]').forEach(handlePromoFill);
      });
      if (m.target.nodeType === 1 && m.target.matches && m.target.matches('[data-body-grid][data-promo-fill]')) {
        scheduleTrigger();
      }
    });
  }).observe(document.body, { childList: true, subtree: true });

  setInterval(function () {
    var grid = document.querySelector('[data-body-grid][data-promo-fill]');
    if (!grid || window.innerWidth < 768) return;
    var fill = parseInt(grid.dataset.promoFill, 10);
    if (!fill) return;
    var currentUrl = window.location.href;
    var fillDone = grid.dataset.promoFillComplete === currentUrl ||
                   grid.querySelectorAll('[data-promo-fill-card]').length >= fill;
    if (currentUrl === lastFillUrl && fillDone) return;
    lastFillUrl = currentUrl;
    if (!fillDone) {
      delete grid.dataset.promoFillDone;
      runPromoFill(grid);
    }
  }, 1000);
}());

// Patch countdown-timer: add .time-out to expired .announcement--block
customElements.whenDefined('countdown-timer').then(function () {
  var CT = customElements.get('countdown-timer');
  if (!CT) return;
  var orig = CT.prototype.checkIfFinished;
  CT.prototype.checkIfFinished = function () {
    var result = orig.call(this);
    if (result) {
      var hideMode = this.getAttribute('data-hide-expired');
      if (hideMode === 'true') {
        var block = this.closest('.announcement--block');
        if (block) block.classList.add('time-out');
      } else if (hideMode === 'only_counter') {
        this.style.display = 'none';
      }
    }
    return result;
  };
});

// Remove target="_blank" from additional links in market-switcher (set by theme's configureLinks)
window.addEventListener('load', function () {
  document.querySelectorAll('.market-switcher__group a').forEach(function (a) {
    a.removeAttribute('target');
  });
});

// Patch PredictiveSearch to include barcode/EAN in search fields
customElements.whenDefined('predictive-search-element').then((PredictiveSearchClass) => {
  PredictiveSearchClass.prototype.getSearchResults = async function (t) {
    this.abort_controller && this.abort_controller.abort();
    var e = t.trim();
    if (this.cached_results[e]) {
      this.results.innerHTML = this.cached_results[e];
    } else {
      this.abort_controller = new AbortController();
      this.toggleLoading(true);
      try {
        var i = encodeURIComponent(t),
          s = await fetch(
            `${Shopify.routes.predictive_search}?q=${i}&section_id=predictive-search&resources[options][fields]=title,product_type,tag,variants.barcode,variants.sku`,
            { signal: this.abort_controller.signal }
          );
        if (!s.ok) throw new Error(s.status);
        var r = (await s.text()).parse().innerHTML;
        this.cached_results[e] = r;
        this.results.innerHTML = r;
      } catch {}
      this.toggleLoading(false);
    }
  };
});

// Article carousel
if (!customElements.get('article-carousel')) {
  class ArticleCarousel extends HTMLElement {
    connectedCallback() {
      if (typeof Swiper !== 'undefined') {
        this._init();
        return;
      }
      const swiperScript = Array.from(document.querySelectorAll('script[src]'))
        .find(s => s.src.includes('swiper'));
      if (swiperScript) {
        swiperScript.addEventListener('load', () => this._init(), { once: true });
      }
    }
    _init() {
      if (this.swiper) return;
      this.swiper = new Swiper(this.querySelector('.swiper-container'), {
        loop: false,
        grabCursor: false,
        speed: 500,
        spaceBetween: 15,
        slidesPerView: 1,
        watchSlidesProgress: true,
        navigation: {
          nextEl: this.querySelector('.arrow--right'),
          prevEl: this.querySelector('.arrow--left'),
        },
        breakpoints: {
          768: { spaceBetween: 20, slidesPerView: 2 },
          992: { spaceBetween: 20, slidesPerView: 3 },
          1200: { spaceBetween: 30, slidesPerView: 3 },
        },
        on: {
          init: (swiper) => {
            this.classList.add('is-initialized');
            this._updateNav(swiper);
          },
          breakpoint: (swiper) => this._updateNav(swiper),
        },
      });
    }
    _updateNav(swiper) {
      const nav = this.querySelector('.article-carousel__header__nav');
      if (!nav) return;
      nav.hidden = swiper.isBeginning && swiper.isEnd;
    }
  }
  customElements.define('article-carousel', ArticleCarousel);
}

// Coupons slider — Swiper
(function () {
  function initCouponsSlider() {
    document.querySelectorAll('.coupons-slider__slider__inner').forEach(function (el) {
      if (el._couponsSwiper) {
        el._couponsSwiper.destroy(true, true);
        el._couponsSwiper = null;
      }

      var wrapper = el.closest('.coupons-slider__wrapper');
      var nav = wrapper ? wrapper.querySelector('.coupons-slider__nav') : null;
      var prevBtn = nav ? nav.querySelector('.coupons-slider__btn-prev') : null;
      var nextBtn = nav ? nav.querySelector('.coupons-slider__btn-next') : null;

      function syncNav(swiper) {
        if (nav) nav.classList.toggle('is-active', !(swiper.isBeginning && swiper.isEnd));
      }

      el._couponsSwiper = new Swiper(el, {
        spaceBetween: 24,
        slidesPerView: 1,
        watchSlidesProgress: true,
        navigation: (prevBtn && nextBtn) ? { prevEl: prevBtn, nextEl: nextBtn } : false,
        on: {
          init: syncNav,
          slideChange: syncNav,
          breakpointChange: syncNav,
          resize: syncNav
        },
        breakpoints: {
          540: { slidesPerView: 2, spaceBetween: 24 },
          992: { slidesPerView: 3, spaceBetween: 24 },
          1400: { slidesPerView: 4, spaceBetween: 32 },
          1600: { slidesPerView: 5, spaceBetween: 32 }
        }
      });
    });
  }

  function tryInit(attempts) {
    if (typeof Swiper !== 'undefined') {
      initCouponsSlider();
    } else if (attempts > 0) {
      setTimeout(function () { tryInit(attempts - 1); }, 100);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { tryInit(20); });
  } else {
    tryInit(20);
  }

  document.addEventListener('shopify:section:load', function (e) {
    if (e.target.querySelector('.coupons-slider__slider')) initCouponsSlider();
  });
})();

// Header small class on scroll
(function () {
  var header = document.querySelector('.header--root');
  if (!header) return;

  var THRESHOLD_DOWN = 172;
  var THRESHOLD_UP = 140;
  var isSmall = false;
  var locked = false;

  function update() {
    if (locked) return;
    var y = window.scrollY;
    if (!isSmall && y > THRESHOLD_DOWN) {
      isSmall = true;
      locked = true;
      header.classList.add('header--small');
      setTimeout(function () { locked = false; }, 400);
    } else if (isSmall && y < THRESHOLD_UP) {
      isSmall = false;
      locked = true;
      header.classList.remove('header--small');
      setTimeout(function () { locked = false; }, 400);
    }
  }

  update();
  window.addEventListener('scroll', update, { passive: true });
})();

// Eveline Slideshow section
(function () {
  if (typeof customElements === 'undefined' || customElements.get('eveline-slideshow')) return;

  var MOBILE_BREAKPOINT = 768;
  var DOT_WINDOW = 5;

  class EvelineSlideshow extends HTMLElement {
    connectedCallback() {
      this.swiperEl = this.querySelector('.carousel--wrapper');
      if (!this.swiperEl) return;

      this.dotWrapper = this.querySelector('.carousel-nav-dot--wrapper');
      this.dots = Array.prototype.slice.call(this.querySelectorAll('.carousel-nav-dot--index'));

      this.autoRotate = this.dataset.autoRotate === 'true';
      this.perView = parseInt(this.dataset.columns, 10) || 1;
      this.perViewMobile = parseInt(this.dataset.mobileColumns, 10) || 1;

      var slideCount = this.querySelectorAll('.carousel--block').length;
      this.loop = this.dataset.loop === 'true' && slideCount > Math.max(this.perView, this.perViewMobile);

      this.onBlockSelect = this.blockSelect.bind(this);
      this.onBlockDeselect = this.blockDeselect.bind(this);
      document.addEventListener('shopify:block:select', this.onBlockSelect);
      document.addEventListener('shopify:block:deselect', this.onBlockDeselect);

      this.tryInit(30);
    }

    disconnectedCallback() {
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
      document.removeEventListener('shopify:block:deselect', this.onBlockDeselect);
      this.destroySwiper();
    }

    tryInit(attempts) {
      var self = this;
      if (typeof Swiper !== 'undefined') {
        this.initSwiper();
      } else if (attempts > 0) {
        setTimeout(function () { self.tryInit(attempts - 1); }, 100);
      }
    }

    destroySwiper() {
      if (this.swiper) {
        this.swiper.destroy(true, true);
        this.swiper = null;
      }
    }

    initSwiper() {
      this.destroySwiper();

      var self = this;
      var frequency = parseFloat(this.dataset.rotateFrequency) || 5;
      var speed = parseInt(this.dataset.transitionSpeed, 10) || 600;

      var handlers = {
        init: function () { self.syncDots(); self.reloadTransitions(); },
        slideChange: function () { self.syncDots(); self.reloadTransitions(); },
        breakpointChange: function () { self.syncDots(); },
        resize: function () { self.syncDots(); },
        autoplayPause: function () { self.setTimerState('paused'); },
        autoplayResume: function () { self.setTimerState('running'); }
      };

      this.swiper = new Swiper(this.swiperEl, {
        init: false,
        slidesPerView: this.perViewMobile,
        slidesPerGroup: this.perViewMobile,
        loop: this.loop,
        speed: speed,
        autoplay: this.autoRotate ? {
          delay: frequency * 1000,
          disableOnInteraction: false,
          pauseOnMouseEnter: true
        } : false,
        breakpoints: {
          768: { slidesPerView: this.perView, slidesPerGroup: this.perView }
        },
        on: handlers
      });

      this.bindDots();
      this.swiper.init();
    }

    setTimerState(state) {
      this.style.setProperty('--animation-play-state', state);
    }

    perGroup() {
      return (this.swiper && this.swiper.params.slidesPerGroup) || 1;
    }

    visibleDots() {
      var mobile = window.innerWidth < MOBILE_BREAKPOINT;
      return this.dots.filter(function (dot) {
        var mq = dot.dataset.mq;
        if (!mq) return true;
        return mobile ? mq === 'small' : mq === 'medium-large';
      });
    }

    bindDots() {
      var self = this;
      this.dots.forEach(function (dot) {
        dot.addEventListener('click', function () {
          var index = self.visibleDots().indexOf(dot);
          if (index < 0) return;
          var slide = index * self.perGroup();
          if (self.loop) self.swiper.slideToLoop(slide);
          else self.swiper.slideTo(slide);
        });
      });
    }

    syncDots() {
      if (!this.swiper || !this.dots.length) return;

      var dots = this.visibleDots();
      var active = Math.floor(this.swiper.realIndex / this.perGroup());
      if (active >= dots.length) active = dots.length - 1;

      this.dots.forEach(function (dot) { dot.setAttribute('aria-current', 'false'); });
      if (dots[active]) dots[active].setAttribute('aria-current', 'true');

      this.updateDotWindow(dots, active);
    }

    updateDotWindow(dots, active) {
      var windowed = window.innerWidth < MOBILE_BREAKPOINT && dots.length > DOT_WINDOW;

      if (!windowed) {
        dots.forEach(function (dot) { dot.removeAttribute('data-position'); });
        if (this.dotWrapper) this.dotWrapper.style.setProperty('--dot-pos', '0px');
        return;
      }

      var last = DOT_WINDOW - 1;
      var start = Math.min(Math.max(active - 2, 0), dots.length - DOT_WINDOW);
      var end = start + last;

      dots.forEach(function (dot, i) {
        var offset = i - start;
        var position = '';

        if (offset < 0 || offset > last) position = 'hidden';
        else if (start > 0 && offset === 0) position = 'trailing-2';
        else if (start > 0 && offset === 1) position = 'trailing-1';
        else if (end < dots.length - 1 && offset === last) position = 'leading-2';
        else if (end < dots.length - 1 && offset === last - 1) position = 'leading-1';

        if (position) dot.setAttribute('data-position', position);
        else dot.removeAttribute('data-position');
      });

      if (this.dotWrapper) {
        var step = dots.length > 1 ? dots[1].offsetLeft - dots[0].offsetLeft : 0;
        this.dotWrapper.style.setProperty('--dot-pos', -(start * step) + 'px');
      }
    }

    activeBlocks() {
      var swiper = this.swiper;
      var perView = swiper.params.slidesPerView;
      var start = swiper.activeIndex;

      if (start + perView > swiper.slides.length) {
        start = Math.max(0, swiper.slides.length - perView);
      }

      return swiper.slides.slice(start, start + perView);
    }

    reloadTransitions() {
      if (typeof window.trigger !== 'function' || !this.swiper) return;

      var active = this.activeBlocks();

      this.swiper.slides.forEach(function (slide) {
        if (slide.dataset.id && active.indexOf(slide) === -1) {
          window.trigger('theme:transition:reset:' + slide.dataset.id);
        }
      });

      active.forEach(function (slide) {
        if (slide.dataset.id) {
          window.trigger('theme:transition:reload:' + slide.dataset.id);
        }
      });
    }

    blockSelect(event) {
      if (!this.swiper || !this.contains(event.target)) return;
      var index = this.swiper.slides.indexOf(event.target);
      if (index < 0) return;
      if (this.swiper.autoplay) this.swiper.autoplay.stop();

      if (this.loop) {
        var real = parseInt(event.target.dataset.swiperSlideIndex, 10);
        this.swiper.slideToLoop(isNaN(real) ? index : real);
      } else {
        this.swiper.slideTo(index);
      }
    }

    blockDeselect(event) {
      if (!this.swiper || !this.contains(event.target)) return;
      if (this.autoRotate && this.swiper.autoplay) this.swiper.autoplay.start();
    }
  }

  customElements.define('eveline-slideshow', EvelineSlideshow);
})();
